import React, { useEffect, useRef, useState } from "react";
import { db, auth } from "../firebase";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  serverTimestamp,
  limit
} from "firebase/firestore";
import "./community.css";
import axios from "axios";
import { useParams, Link } from "react-router-dom";

/* ==========================================================
   AI SMART AUTO-ENGAGE ENGINE (Same logic as Thread.js)
   ========================================================== */

const careerKeywords = [
  "resume", "interview", "apply", "job", "career", "promotion",
  "salary", "offer", "linkedin", "experience", "role",
  "interviewer", "hiring manager", "opportunity",
  "skills", "certification", "work", "professional",
];

function shouldAIRespond(text, messages) {
  const lower = text.toLowerCase().trim();

  // Too short / pointless
  if (lower.length < 15) return false;

  // Needs a question or a keyword
  const hasIntent =
    lower.includes("?") ||
    careerKeywords.some((k) => lower.includes(k));

  if (!hasIntent) return false;

  // Avoid multiple consecutive AI responses
  const aiAlready = messages.slice(-5).some((m) => m.isAI);
  if (aiAlready && !lower.includes("follow")) return false;

  return true;
}

function routeToTool(text) {
  const l = text.toLowerCase();

  if (l.includes("resume") || l.includes("cv")) return "resume";
  if (l.includes("interview") || l.includes("hr")) return "interview";

  return "career";
}

function formatAIResponse(raw) {
  return (
    raw +
    "\n\nIf you'd like more tailored guidance, share your experience level or target role."
  );
}

/* ==========================================================
   CHATROOM COMPONENT
   ========================================================== */

function ChatRoom() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const messagesEndRef = useRef(null);

  const apiBase = process.env.REACT_APP_API_URL;

  // Auto-scroll
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(scrollToBottom, [messages]);

  /* ==========================================================
     🔵 LIVE CHAT LISTENER (Realtime DB)
     ========================================================== */
  useEffect(() => {

  const q = query(
    collection(db, "community_chat"),
    orderBy("timestamp", "asc"),
    limit(200)
  );

  const unsubscribe = onSnapshot(q, (snapshot) => {

    const list = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    setMessages(list);

  });

  return () => unsubscribe();

}, []);

  /* ==========================================================
     🟢 SEND MESSAGE + AI AUTO RESPONSE
     ========================================================== */
  const sendMessage = async () => {
    const user = auth.currentUser;
    if (!user || input.trim() === "") return;

    const msg = input.trim();
    setInput("");

    // Save user message
    await addDoc(collection(db, "community_chat"), {
  text: msg,
  user: user.email,
  uid: user.uid,
  timestamp: Date.now(),
  createdAt: serverTimestamp(),
  isAI: false,
});

    scrollToBottom();

    /* ---------------------------------------------------------
       SMART AI AUTO-ENGAGE
       --------------------------------------------------------- */
    const allowAI = shouldAIRespond(msg, messages);

    if (!allowAI) return;

    const tool = routeToTool(msg);

    let endpoint = "/api/ai-career-advisor";
    if (tool === "resume") endpoint = "/api/ai-resume-critique";
    if (tool === "interview") endpoint = "/api/ai-interview-coach";

    try {
      // Human-like delay
      await new Promise((r) =>
        setTimeout(r, 3500 + Math.random() * 3000)
      );

      const aiRes = await axios.post(`${apiBase}${endpoint}`, {
        topic: msg,
        question: msg,
        resume: msg,
      });

      const raw =
        aiRes.data.advice ||
        aiRes.data.coach ||
        aiRes.data.analysis ||
        "I'm here to help with anything career-related!";

      const finalText = formatAIResponse(raw);

      await addDoc(collection(db, "community_chat"), {
  text: finalText,
  user: "EmployPilot AI",
  uid: "ai_bot",
  timestamp: Date.now(),
  createdAt: serverTimestamp(),
  isAI: true,
});
    } catch (err) {
      console.error("AI chat error:", err);
    }
  };

  /* ==========================================================
     UI
     ========================================================== */
  return (
    <div className="chat-wrapper">
      <Link to="/community">
  <button className="back-btn">← Back to Community</button>
</Link>
      <h2 className="chat-title">💬 EmployPilot Live Career Chatroom</h2>
      <p className="chat-sub">Ask career questions and get real-time help.</p>

      {/* Chat Window */}
      <div className="chat-box">
        {messages.map((msg, i) => (
          <div key={i} className={msg.isAI ? "ai-bubble" : "user-bubble"}>
            <p className="bubble-text">{msg.text}</p>
            <span className="bubble-meta">
              {msg.user} •{" "}
              {new Date(msg.timestamp).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="chat-input-bar">
        <input
          type="text"
          placeholder="Type a message..."
          className="chat-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && sendMessage()}
        />
        <button className="chat-send" onClick={sendMessage}>
          Send
        </button>
      </div>
    </div>
  );
}

export default ChatRoom;

